import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import toast from '../toast'
import { extractErrorMessage } from '../errorHelpers'

export default function useMessages(conversationId) {
  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(1)

  const fetchMessages = useCallback(
    async (pageNum = 1, append = false) => {
      if (!conversationId) return

      try {
        setIsLoading(true)
        setError(null)
        const response = await axios.get(`/api/conversations/${conversationId}`, {
          params: { page: pageNum }
        })

        const conversationData = response.data.data
        const messagesWithSender = conversationData.attributes?.messages || []

        if (append) {
          setMessages((prev) => [...prev, ...messagesWithSender])
        } else {
          setMessages(messagesWithSender)
        }

        setHasMore(messagesWithSender.length === 200)
      } catch (err) {
        console.error('Error fetching messages:', err)
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    },
    [conversationId]
  )

  useEffect(() => {
    if (conversationId) {
      setPage(1)
      fetchMessages(1, false)
    }
  }, [conversationId, fetchMessages])

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchMessages(nextPage, true)
    }
  }, [isLoading, hasMore, page, fetchMessages])

  const sendMessage = useCallback(
    async (body) => {
      if (!conversationId) return

      try {
        const response = await axios.post(`/api/conversations/${conversationId}/messages`, {
          body
        })
        const newMessage = response.data.data
        setMessages((prev) => [newMessage, ...prev])
        return newMessage
      } catch (err) {
        const errorMessage = extractErrorMessage(err)
        toast.error(errorMessage)
        throw err
      }
    },
    [conversationId]
  )

  const markAsRead = useCallback(async (messageId) => {
    try {
      await axios.put(`/api/messages/${messageId}`, {
        mark_as_read: true
      })
    } catch (err) {
      console.error('Error marking message as read:', err)
    }
  }, [])

  const addMessage = useCallback((newMessage) => {
    setMessages((prev) => {
      const exists = prev.find((m) => m.id === newMessage.id)
      if (!exists) {
        return [newMessage, ...prev]
      }
      return prev
    })
  }, [])

  const updateMessage = useCallback((messageId, updates) => {
    setMessages((prev) => prev.map((message) => (message.id === messageId ? { ...message, ...updates } : message)))
  }, [])

  return {
    messages,
    isLoading,
    error,
    hasMore,
    loadMore,
    sendMessage,
    markAsRead,
    addMessage,
    updateMessage,
    refetch: () => fetchMessages(1, false)
  }
}
